/**
 * Module dependencies.
 */

import BigNumber from 'bignumber.js';
import { TFunction } from 'i18next';
import { get } from 'lodash';
import { FieldValues, Path, PathValue } from 'react-hook-form';

/**
 * Export `requiredRule`.
 */

export const requiredRule = (translate: TFunction) => ({
  required: translate('common.errors.form.required')
});

/**
 * Export `exclusiveRule`.
 */

export const exclusiveRule = <F extends FieldValues, P extends Path<F>, O extends Path<F>>(
  translate: TFunction,
  otherField: { field: O; label: string } | O,
  required = false
) => ({
  required: false,
  validate: {
    exclusive: (value: PathValue<F, P>, form: F) => {
      const other = typeof otherField === 'string' ? { field: otherField, label: otherField } : otherField;
      const otherValue = get(form, other.field);

      if (!!otherValue && !!value) {
        return translate('common.errors.form.exclusive', { other: other.label });
      }

      if (required && !value && !otherValue) {
        return translate('common.errors.form.exclusiveRequired', { other: other.label });
      }
    }
  },
  deps: typeof otherField === 'string' ? otherField : otherField.field
});

/**
 * Export `validatePasswordRule`.
 */

export const validatePasswordRule = (translate: TFunction, password: string) => ({
  required: translate('common.errors.form.required'),
  validate: (val: string) => {
    return password === val || translate('common.errors.form.passwordNotMatch');
  }
});

/**
 * Export `numericRule`.
 */

export const numericRule = (translate: TFunction) => ({
  required: translate('common.errors.form.required'),
  validate: (val: string) => {
    return !new BigNumber(val).isNaN() || translate('common.errors.form.numeric');
  }
});
